/**
 * Cliente da API do cardápio.
 *
 * O cardápio é aberto — ver a loja, montar carrinho e acompanhar pedido não
 * pedem nada. A exceção é a área do cliente, em /v1/eu, que anda com um cookie
 * de sessão. Por isso o `credentials: 'include'`: sem ele o navegador não
 * manda nem guarda esse cookie quando a API está noutro host, e a conta
 * abriria vazia sem erro nenhum na tela.
 *
 * O cookie é httpOnly, escopado em /v1/eu e sameSite strict; o CORS da API tem
 * allowlist exata. Nada disso vale para as rotas públicas, que seguem sem
 * sessão.
 */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: { campo: string; erro: string }[],
  ) {
    super(mensagem)
    this.name = 'ErroApi'
  }
}

export const BASE_API = import.meta.env.VITE_API_URL ?? 'http://localhost:3333'

export async function api<T = unknown>(
  caminho: string,
  opcoes: Omit<RequestInit, 'body'> & { corpo?: unknown } = {},
): Promise<T> {
  const { corpo, ...resto } = opcoes
  const r = await fetch(`${BASE_API}${caminho}`, {
    credentials: 'include',
    ...resto,
    headers: {
      ...(corpo !== undefined ? { 'content-type': 'application/json' } : {}),
      ...resto.headers,
    },
    ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
  })

  if (r.status === 204) return undefined as T
  const texto = await r.text()
  const dados = texto ? JSON.parse(texto) : null
  if (!r.ok) {
    const e = dados?.erro ?? {}
    throw new ErroApi(r.status, e.codigo ?? 'ERRO', e.mensagem ?? 'Falha na comunicação.', e.detalhes)
  }
  return dados as T
}

export type Adicional = { id: string; nome: string; precoCents: number; esgotado: boolean }
export type Grupo = {
  id: string
  nome: string
  min: number
  max: number
  obrigatorio: boolean
  adicionais: Adicional[]
}
export type Produto = {
  id: string
  nome: string
  slug: string
  ingredientes: string | null
  precoCents: number
  fotoUrl: string | null
  tipo: 'SIMPLES' | 'COMBO'
  esgotado: boolean
  destaque: boolean
  combo: { nome: string | null; quantidade: number }[]
  grupos: Grupo[]
}
export type Categoria = {
  id: string
  nome: string
  slug: string
  descricao: string | null
  produtos: Produto[]
}
export type Loja = {
  nome: string
  telefone: string
  whatsapp: string
  endereco: { rua: string; numero: string; bairro: string; cidade: string; uf: string }
  abertaDelivery: boolean
  abertaBalcao: boolean
  deliveryPausado: boolean
  pedidoMinimoCents: number
  tempoPreparoMin: number
  modoTaxa: string
  /** Quando true, "Pix" no checkout significa pagar agora, com código na tela. */
  pixOnline: boolean
  /** Só o booleano: a régua fica no servidor, senão a tela diverge do extrato. */
  fidelidadeAtiva: boolean
  horarios: { diaSemana: number; canal: string; abreMin: number; fechaMin: number }[]
}
export type Bairro = { id: string; nome: string; taxaCents: number; tempoEstimadoMin: number }
